import { defineTable } from "convex/server";
import { v } from "convex/values";
import { internalControl } from "../../../lib/shared/validators";

export const packageStatus = v.union(
  v.literal("box_delivered"),
  v.literal("received"),
  v.literal("consolidated"),
  v.literal("in_transit"),
  v.literal("delivered"),
  v.literal("returned"),
);

export const packagePaymentStatus = v.union(
  v.literal("pending"),
  v.literal("advance_paid"),
  v.literal("partial"),
  v.literal("completed"),
);

export const validatorPackage = v.object({
  senderId: v.id("clients"),
  recipientId: v.optional(v.id("recipients")),
  recipientAddressIndex: v.optional(v.number()),
  // Short 4-digit number for operations (e.g., "0273") - resets monthly
  orderNumber: v.string(),
  // Sequential number assigned when package is consolidated (e.g., "0001")
  consolidationNumber: v.optional(v.union(v.string(), v.null())),
  trackingNumber: v.optional(v.string()),
  carrierId: v.optional(v.id("carriers")),
  boxTypeId: v.optional(v.id("box_types")),
  realLength: v.number(),
  realWidth: v.number(),
  realHeight: v.number(),
  volumeCF: v.number(),
  volumetricWeight: v.number(),
  excessVolumeCF: v.number(),
  realWeight: v.optional(v.number()),
  declaredValue: v.number(),
  price: v.number(),
  additionalCharge: v.optional(v.number()),
  photoStorageId: v.optional(v.id("_storage")),
  qrCode: v.string(),
  shipmentId: v.optional(v.union(v.id("shipments"), v.null())),
  logisticsOrderId: v.optional(v.id("logistics_orders")),
  status: packageStatus,
  merchandiseDescription: v.optional(v.string()),
  notes: v.optional(v.string()),
  boxDeliveredAt: v.optional(v.number()),
  boxDeliveredBy: v.optional(v.id("users")),
  advancePayment: v.optional(v.number()),
  paymentStatus: v.optional(packagePaymentStatus),
  verificationQrCode: v.optional(v.string()),
  // Pickup evidence (box_delivered -> received)
  pickupPhotos: v.optional(v.array(v.id("_storage"))),
  pickupNotes: v.optional(v.string()),
  pickedUpAt: v.optional(v.number()),
  pickedUpBy: v.optional(v.id("users")),
  // Delivery evidence (in_transit -> delivered)
  deliveryPhotos: v.optional(v.array(v.id("_storage"))),
  deliverySignature: v.optional(v.id("_storage")),
  deliveryNotes: v.optional(v.string()),
  deliveredAt: v.optional(v.number()),
  deliveredBy: v.optional(v.id("users")),
  receivedByName: v.optional(v.string()),
  receivedByDocument: v.optional(v.string()),
  // Delivery attempts tracking
  deliveryAttemptCount: v.optional(v.number()),
  lastFailedAttemptAt: v.optional(v.number()),
  lastFailedAttemptReason: v.optional(v.string()),
  internalControl: internalControl,
});

export const packages = defineTable(validatorPackage)
  .index("by_company_id", ["internalControl.companyId"])
  .index("by_branch_id", ["internalControl.branchId"])
  .index("by_state", ["internalControl.state"])
  .index("by_company_and_state", [
    "internalControl.companyId",
    "internalControl.state",
  ])
  .index("by_company_and_branch", [
    "internalControl.companyId",
    "internalControl.branchId",
  ])
  .index("by_branch_and_state", [
    "internalControl.branchId",
    "internalControl.state",
  ])
  .index("by_created_by", ["internalControl.createdBy"])
  .index("by_updated_by", ["internalControl.updatedBy"])
  .index("by_created_at", ["internalControl.createdAt"])
  .index("by_company_and_created_at", [
    "internalControl.companyId",
    "internalControl.createdAt",
  ])
  .index("by_status", ["status"])
  .index("by_company_and_status", ["internalControl.companyId", "status"])
  .index("by_sender", ["senderId"])
  .index("by_recipient", ["recipientId"])
  .index("by_company_and_sender", ["internalControl.companyId", "senderId"])
  .index("by_company_and_recipient", [
    "internalControl.companyId",
    "recipientId",
  ])
  .index("by_shipment", ["shipmentId"])
  .index("by_company_and_shipment", ["internalControl.companyId", "shipmentId"])
  .index("by_logistics_order", ["logisticsOrderId"])
  .index("by_company_and_logistics_order", [
    "internalControl.companyId",
    "logisticsOrderId",
  ])
  .index("by_order_number", ["orderNumber"])
  .index("by_company_and_order_number", [
    "internalControl.companyId",
    "orderNumber",
  ])
  .index("by_tracking_number", ["trackingNumber"])
  .index("by_qr_code", ["qrCode"])
  .index("by_carrier", ["carrierId"])
  .index("by_box_type", ["boxTypeId"])
  .index("by_verification_qr_code", ["verificationQrCode"])
  .index("by_payment_status", ["paymentStatus"])
  .index("by_company_and_payment_status", [
    "internalControl.companyId",
    "paymentStatus",
  ])
  .searchIndex("search_packages", {
    searchField: "orderNumber",
    filterFields: [
      "internalControl.companyId",
      "internalControl.state",
      "status",
      "shipmentId",
      "logisticsOrderId",
    ],
  });
